// Issue with watermelonDB library https://github.com/Nozbe/WatermelonDB/issues/629
// @ts-nocheck
import { Model, Q } from '@nozbe/watermelondb';
import {
    field,
    date,
    readonly,
    relation,
    lazy,
} from '@nozbe/watermelondb/decorators';
import { setGenerator } from '@nozbe/watermelondb/utils/common/randomId';
import { v4 as uuidv4 } from 'uuid';
import { InvoiceInvoicingProductSimple } from './InvoiceInvoicingProduct';
import { InvoiceModel } from '../../types/Invoice';
import { AccountingProviderType } from '../../types/Invoicing';

setGenerator(uuidv4);

export default class Invoice extends Model {
    static table = 'invoices';

    static associations = {
        contacts: { type: 'belongs_to', key: 'contact_id' },
    };

    @relation('users', 'user_id') user;
    @relation('organisations', 'organisation_id') organisation;
    @relation('contacts', 'contact_id') contact;

    @field('user_id') userId;
    @field('organisation_id') organisationId;
    @field('contact_id') contactId;
    @field('external_id') externalId;
    @field('deep_link') deepLink;
    @field('number') number;
    @field('status') status;
    @field('footer_text') footerText;
    @field('sub_total') subTotal;
    @field('total_tax') totalTax;
    @field('total') total;
    @field('total_discount') totalDiscount;
    @field('sent_to_contact') sentToContact;
    @field('authorised_time') authorisedTime;
    @field('due_date_time') dueDateTime;
    @field('sent_to_contact_time') sentToContactTime;
    @field('payment_date_time') paymentDateTime;
    @field('reference') reference;
    @field('apply_taxes') applyTaxes;
    @field('attach_reports') attachReports;
    @field('public_url') publicUrl;
    @field('amount_check') amountCheck;
    @field('amount_valid') amountValid;
    @field('synced') synced;
    @field('provider') provider;
    @field('tax_rate') taxRate;
    @field('products_tax_rate') productsTaxRate;
    @field('provider') provider;
    @field('invoice_date') invoiceDate;
    @field('user_provider_email') userProviderEmail;
    @field('provider_archived_at') providerArchivedAt;
    @field('last_synced_at') lastSyncAt;
    @date('created_at') createdAt;
    @date('updated_at') updatedAt;

    @lazy
    entries = this.collections
        .get('entries')
        .query(Q.where('invoice_id', this.id));

    @lazy
    contacts = this.collections
        .get('contacts')
        .query(Q.where('id', this.contactId));

    @lazy
    products = this.collections
        .get('invoice_invoicing_products')
        .query(Q.where('invoice_id', this.id))
        .extend(Q.sortBy('created_at', Q.desc));
}

export type InvoiceSimple = {
    userId: string;
    organisationId: string;
    contactId: string;
    id: string;
    externalId: string;
    deepLink: string;
    number: string;
    status: string;
    footerText: string;
    subTotal: string;
    totalTax: string;
    total: string;
    totalDiscount: string;
    sentToContact: boolean;
    authorisedTime: string;
    dueDateTime: string;
    sentToContactTime: string;
    paymentDateTime: string;
    reference: string;
    applyTaxes: boolean;
    attachReports: boolean;
    publicUrl: string;
    amountCheck: string;
    amountValid: boolean;
    synced: boolean;
    provider?: AccountingProviderType;
    taxRate: string;
    productsTaxRate: string;
    invoiceDate: string;
    userProviderEmail: string;
    providerArchivedAt: string;
    lastSyncedAt: string;
    createdAt: string;
    updatedAt: string;
} & InvoiceSimpleRelations;

export type InvoiceSimpleRelations = {
    entriesIds?: string[];
    invoicingProducts?: InvoiceInvoicingProductSimple[];
};

export const simplifyInvoice = (
    invoice: InvoiceModel,
    relations?: InvoiceSimpleRelations,
): InvoiceSimple => {
    return {
        userId: invoice.userId,
        organisationId: invoice.organisationId,
        contactId: invoice.contactId,
        id: invoice.id,
        externalId: invoice.externalId,
        deepLink: invoice.deepLink,
        number: invoice.number,
        status: invoice.status,
        footerText: invoice.footerText,
        subTotal: invoice.subTotal,
        totalTax: invoice.totalTax,
        total: invoice.total,
        totalDiscount: invoice.totalDiscount,
        sentToContact: invoice.sentToContact,
        authorisedTime: invoice.authorisedTime,
        dueDateTime: invoice.dueDateTime,
        sentToContactTime: invoice.sentToContactTime,
        paymentDateTime: invoice.paymentDateTime,
        reference: invoice.reference,
        applyTaxes: invoice.applyTaxes,
        attachReports: invoice.attachReports,
        publicUrl: invoice.publicUrl,
        amountCheck: invoice.amountCheck,
        amountValid: invoice.amountValid,
        synced: invoice.synced,
        provider: invoice.provider,
        taxRate: invoice.taxRate,
        productsTaxRate: invoice.productsTaxRate,
        invoiceDate: invoice.invoiceDate,
        userProviderEmail: invoice.userProviderEmail,
        providerArchivedAt: invoice.providerArchivedAt,
        lastSyncedAt: invoice.lastSyncAt,
        createdAt: invoice.createdAt.toISOString(),
        updatedAt: invoice.updatedAt.toISOString(),
        ...relations,
    };
};
